<template>
  <div class="record-container" :class="{ 'max-retries': isMaxRetries }">

    <!-- Collapsed header row -->
    <div class="record-header" @click="toggleExpanded">
      <button class="arrow-button">{{ isExpanded ? '▾' : '▸' }}</button>
      <span class="timestamp">{{ formattedTimestamp }}</span>
      <span class="caller-id">{{ prettyPhone(record.callerId) }}</span>
      <span class="record-info">
        <span class="badge" :class="callTypeBadgeClass">{{ callTypeLabel }}</span>
        <span class="badge" :class="outcomeBadgeClass">{{ formattedOutcome }}</span>
        <span v-if="record.digitSource === 'override'" class="badge badge-override">⚑ OVR</span>
        <span class="dtmf-info">{{ dtmfInfo }}</span>
        <span class="duration">{{ formattedDuration }}</span>
      </span>
      <span class="call-sid-header">{{ record.callSid }}</span>
    </div>

    <!-- Expanded content -->
    <div v-if="isExpanded" class="expanded-content">

      <!-- Section A: Recording -->
      <div class="expanded-section">
        <div class="section-label">Recording</div>
        <div class="channel-controls">
          <label class="channel-label">
            <input
              type="radio"
              :name="`channel-${recordId}`"
              value="both"
              v-model="selectedChannel"
              @change="updateChannelMix"
            >
            Both Channels
          </label>
          <label class="channel-label">
            <input
              type="radio"
              :name="`channel-${recordId}`"
              value="left"
              v-model="selectedChannel"
              @change="updateChannelMix"
            >
            Left Channel Only
          </label>
          <label class="channel-label">
            <input
              type="radio"
              :name="`channel-${recordId}`"
              value="right"
              v-model="selectedChannel"
              @change="updateChannelMix"
            >
            Right Channel Only
          </label>
        </div>
        <audio
          :ref="`audioPlayer-${recordId}`"
          :src="publicURL"
          controls
          class="audio-player"
          @play="setupWebAudio"
          crossorigin="anonymous"
        >
          Your browser does not support the audio element.
        </audio>
      </div>

      <!-- Section B: Transcript -->
      <div class="expanded-section">
        <div class="section-label">Transcript</div>
        <div class="transcript-box" :class="{ 'empty': !record.transcript }">
          {{ record.transcript || 'No speech detected.' }}
        </div>
      </div>

      <!-- Section C: AI Query Timeline (jail calls only) -->
      <div v-if="record.aiQueries && record.aiQueries.length > 0" class="expanded-section">
        <div class="section-label">AI Query Timeline</div>
        <div class="timeline">
          <div
            v-for="query in record.aiQueries"
            :key="query.queryNumber"
            class="timeline-item"
          >
            <div class="timeline-row">
              <span class="timeline-time">{{ relativeTime(query.timestamp) }}</span>
              <span class="query-number">Query #{{ query.queryNumber }}</span>
              <span class="confidence-badge" :class="confidenceBadgeClass(query.response)">
                {{ confidenceLabel(query.response) }}
              </span>
              <span v-if="query.response.digit" class="digit-chip">
                Digit: {{ query.response.digit }}
              </span>
            </div>
            <div class="transcript-fragment">{{ query.transcript }}</div>
            <div class="reasoning-text">{{ query.response.reasoning }}</div>
          </div>
        </div>
      </div>

      <!-- Section D: State Machine Timeline -->
      <div class="expanded-section">
        <div class="section-label">State Machine Timeline</div>
        <div class="timeline">
          <div
            v-for="(event, index) in record.events"
            :key="index"
            class="timeline-item"
            :class="{ 'timeline-item-override': isOverrideEvent(event) }"
          >
            <div class="timeline-row">
              <span class="timeline-time">{{ deltaTime(index) }} {{ relativeTime(event.timestamp) }}</span>
              <span class="state-badge">{{ event.state }}</span>
              <span v-if="isOverrideEvent(event)" class="override-event-badge">⚑ OVERRIDE</span>
            </div>
            <div class="event-note">{{ event.note }}</div>
          </div>
        </div>
      </div>

      <!-- Section E: Diagnostics -->
      <div class="expanded-section">
        <div class="section-label">Diagnostics</div>
        <div class="diagnostics-grid">
          <span class="diag-key">STT Provider</span>
          <span class="diag-value">{{ record.sttProvider || '—' }}</span>
          <span class="diag-key">AI Provider</span>
          <span class="diag-value">{{ record.aiProvider || '—' }}</span>
          <span class="diag-key">Prompt Version</span>
          <span class="diag-value">{{ record.promptVersion || '—' }}</span>
          <span class="diag-key">DTMF Outcome</span>
          <span class="diag-value">{{ record.dtmfOutcome || '—' }}</span>
          <template v-if="record.digitSource">
            <span class="diag-key">Digit Source</span>
            <span class="diag-value" :class="{ 'diag-override': record.digitSource === 'override' }">
              {{ record.digitSource === 'override' ? '⚑ OVERRIDE' : 'AI' }}
            </span>
          </template>
          <span class="diag-key">Component</span>
          <span class="diag-value">{{ record.component || '—' }}</span>
          <span class="diag-key">Call SID</span>
          <span class="diag-value mono">{{ record.callSid }}</span>
        </div>
      </div>

    </div>
  </div>
</template>


<script>
import { prettyPhone } from '../utils/helpers';
import moment from 'moment-timezone';
import { mapState } from 'vuex';

export default {
  name: 'RawCallDiagnosticsRecord',

  props: {
    record: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      isExpanded: false,
      selectedChannel: 'both',
      audioContext: null,
      sourceNode: null,
      splitterNode: null,
      mergerNode: null,
      gainNodes: [],
      isWebAudioSetup: false
    };
  },

  computed: {
    ...mapState(['selectedCompany']),

    recordId() {
      return this.record.callSid || `record-${Math.random().toString(36).substr(2, 9)}`;
    },

    isMaxRetries() {
      return this.record.dtmfOutcome === 'MAX_RETRIES_EXCEEDED';
    },

    publicURL() {
      return `https://bbasrec.us/${this.record.callSid}_stereo.wav`;
    },

    formattedTimestamp() {
      return moment(this.record.timestamp)
        .tz(this.selectedCompany.timezone)
        .format('ddd · MMM D, h:mm:ss A');
    },

    formattedDuration() {
      const seconds = this.record?.durationSeconds ?? null;
      if (seconds === null) return '—';
      if (seconds >= 60) {
        const minutes = Math.floor(seconds / 60);
        const remaining = seconds % 60;
        return `${minutes}m ${remaining}s`;
      }
      return `${seconds}s`;
    },

    formattedOutcome() {
      const map = {
        'MENU_SUBSYSTEM': 'MENU',
        'VOICEMAIL_SUBSYSTEM': 'VOICEMAIL',
        'FORWARD': 'FORWARD',
        'FAILED': 'FAILED'
      };
      return map[this.record.outcome] || this.record.outcome || '—';
    },

    dtmfInfo() {
      if (this.record.callType === 'BAIL') return '—';
      const repeats = this.record.dtmfAttempts || 0;
      const injections = repeats + 1;
      const s = injections !== 1 ? 's' : '';
      if (this.record.dtmfDigit) {
        return `Digit: ${this.record.dtmfDigit}, ${injections} injection${s}`;
      }
      return '—';
    },

    callTypeLabel() {
      return this.record.callType === 'UNKNOWN' ? 'HANGUP' : this.record.callType;
    },

    callTypeBadgeClass() {
      const map = {
        'BAIL':            'badge-bail',
        'JAIL':            'badge-jail',
        'JAIL_FREE':       'badge-jail-free',
        'JAIL_UNRESOLVED': 'badge-jail-unresolved',
        'UNKNOWN':         'badge-unknown'
      };
      return map[this.record.callType] || 'badge-unknown';
    },

    outcomeBadgeClass() {
      const map = {
        'MENU_SUBSYSTEM':     'badge-menu',
        'VOICEMAIL_SUBSYSTEM': 'badge-voicemail',
        'FORWARD':             'badge-forward',
        'FAILED':              'badge-failed'
      };
      return map[this.record.outcome] || 'badge-unknown';
    }
  },

  methods: {
    prettyPhone,

    isOverrideEvent(event) {
      return !!(event.note && event.note.startsWith('Facility override matched:'));
    },

    toggleExpanded() {
      this.isExpanded = !this.isExpanded;
    },

    relativeTime(timestamp) {
      if (!this.record.events || this.record.events.length === 0) return '—';
      const start    = new Date(this.record.events[0].timestamp);
      const t        = new Date(timestamp);
      const totalSec = (t - start) / 1000;
      const totalMin = Math.floor(totalSec / 60);
      const totalS   = (totalSec % 60).toFixed(1).padStart(4, '0');
      return `${String(totalMin).padStart(2,'0')}:${totalS}s`;
    },

    deltaTime(index) {
      if (!this.record.events || index === 0) return '+0.0s';
      const prev = new Date(this.record.events[index - 1].timestamp);
      const curr = new Date(this.record.events[index].timestamp);
      return `Δ ${((curr - prev) / 1000).toFixed(1)}s`;
    },

    confidenceBadgeClass(response) {
      if (response.insufficientData) return 'conf-insufficient';
      const map = {
        'high':   'conf-high',
        'medium': 'conf-medium',
        'low':    'conf-low'
      };
      return map[response.confidence] || 'conf-insufficient';
    },

    confidenceLabel(response) {
      if (response.insufficientData) return 'INSUFFICIENT';
      return (response.confidence || 'unknown').toUpperCase();
    },

    // --- Web Audio (matches JailCallRecord exactly) ---

    setupWebAudio() {
      if (this.isWebAudioSetup) return;

      try {
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)();

        if (this.audioContext.state === 'suspended') {
          this.audioContext.resume();
        }

        const audioElement = this.$refs[`audioPlayer-${this.recordId}`];
        if (!audioElement) return;

        this.sourceNode = this.audioContext.createMediaElementSource(audioElement);

        setTimeout(() => {
          this.setupAudioRouting();
        }, 100);

      } catch (error) {
        console.error('Web Audio API setup failed:', error);
      }
    },

    setupAudioRouting() {
      try {
        this.splitterNode = this.audioContext.createChannelSplitter(2);
        this.mergerNode   = this.audioContext.createChannelMerger(2);

        const leftGain  = this.audioContext.createGain();
        const rightGain = this.audioContext.createGain();
        this.gainNodes  = [leftGain, rightGain];

        this.sourceNode.connect(this.splitterNode);
        this.splitterNode.connect(leftGain,  0, 0);
        this.splitterNode.connect(rightGain, 1, 0);
        this.mergerNode.connect(this.audioContext.destination);

        this.updateChannelMix();
        this.isWebAudioSetup = true;

      } catch (error) {
        console.error('Audio routing setup failed:', error);
      }
    },

    updateChannelMix() {
      if (!this.gainNodes.length) return;

      const [leftGain, rightGain] = this.gainNodes;

      if (this.audioContext && this.audioContext.state === 'suspended') {
        this.audioContext.resume();
      }

      try {
        leftGain.disconnect();
        rightGain.disconnect();
      } catch (e) {
        // ignore
      }

      switch (this.selectedChannel) {
        case 'left':
          leftGain.gain.value  = 1;
          rightGain.gain.value = 0;
          leftGain.connect(this.mergerNode, 0, 0);
          leftGain.connect(this.mergerNode, 0, 1);
          break;
        case 'right':
          leftGain.gain.value  = 0;
          rightGain.gain.value = 1;
          rightGain.connect(this.mergerNode, 0, 0);
          rightGain.connect(this.mergerNode, 0, 1);
          break;
        case 'both':
        default:
          leftGain.gain.value  = 1;
          rightGain.gain.value = 1;
          leftGain.connect(this.mergerNode,  0, 0);
          rightGain.connect(this.mergerNode, 0, 1);
          break;
      }
    }
  },

  beforeUnmount() {
    if (this.audioContext) {
      this.audioContext.close();
    }
  }
};
</script>


<style scoped>

/* ── Container ─────────────────────────────────────────────────── */
.record-container {
  border: 1px solid #d2b48c;
  margin-bottom: 10px;
  border-radius: 4px;
}

/* ── Collapsed header ───────────────────────────────────────────── */
.record-header {
  font-size: 14px;
  background-color: #f5f5dc;
  padding: 8px 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.max-retries .record-header {
  background-color: #fde8e8;
}

.arrow-button {
  background: none;
  border: none;
  color: #8b4513;
  font-size: 18px;
  cursor: pointer;
  user-select: none;
  -webkit-user-select: none;
  transform: scale(2.0);
  transform-origin: center;
  line-height: 1;
  vertical-align: middle;
  padding: 0;
  margin-right: 5px;
}

.timestamp {
  color: gray;
  min-width: 105px;
}

.caller-id {
  min-width: 105px;
  font-weight: 500;
}

.record-info {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-grow: 1;
  flex-wrap: wrap;
}

.duration {
  color: #666;
  min-width: 50px;
}

.dtmf-info {
  color: #555;
  font-size: 13px;
}

.call-sid-header {
  color: green;
  font-family: monospace;
  font-size: 13px;
}

/* ── Badges ─────────────────────────────────────────────────────── */
.badge {
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  color: white;
}

.badge-bail            { background-color: #4a90d9; }
.badge-jail            { background-color: #e8820c; }
.badge-jail-free       { background-color: #4caf50; }
.badge-jail-unresolved { background-color: #9e9e9e; }
.badge-unknown         { background-color: #bdbdbd; }

.badge-override { background-color: #c8820c; }

.badge-menu      { background-color: #4caf50; }
.badge-voicemail { background-color: #7b68ee; }
.badge-forward   { background-color: #e8820c; }
.badge-failed    { background-color: #f44336; }

/* ── Expanded content wrapper ───────────────────────────────────── */
.expanded-content {
  background-color: #ffffe0;
  display: flex;
  flex-direction: column;
}

.expanded-section {
  padding: 14px 16px;
  border-bottom: 1px solid #e8e0c8;
}

.expanded-section:last-child {
  border-bottom: none;
}

.section-label {
  font-size: 11px;
  font-weight: 700;
  color: #8b4513;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  padding-bottom: 8px;
  border-bottom: 1px solid #d2b48c;
  margin-bottom: 12px;
}

/* ── Section A: Audio ───────────────────────────────────────────── */
.channel-controls {
  display: flex;
  gap: 20px;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 12px;
}

.channel-label {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  padding: 6px 10px;
  border-radius: 4px;
  background-color: #f5f5dc;
  border: 1px solid #d2b48c;
  transition: background-color 0.2s;
  font-size: 14px;
  color: #8b4513;
}

.channel-label:hover {
  background-color: #f0e68c;
}

.channel-label input[type="radio"] {
  accent-color: #8b4513;
  margin: 0;
}

.audio-player {
  width: 100%;
  max-width: 800px;
  display: block;
  margin: 0 auto;
}

/* ── Section B: Transcript ──────────────────────────────────────── */
.transcript-box {
  background-color: #fffff8;
  border: 1px solid #d2b48c;
  border-radius: 4px;
  padding: 10px 14px;
  font-size: 14px;
  line-height: 1.7;
  color: #333;
  min-height: 40px;
}

.transcript-box.empty {
  color: #999;
  font-style: italic;
}

/* ── Sections C & D: Timelines ──────────────────────────────────── */
.timeline {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.timeline-item {
  border-left: 3px solid #d2b48c;
  padding: 6px 12px;
  background-color: #fafaf0;
  border-radius: 0 4px 4px 0;
}

.timeline-item-override {
  border-left: 3px solid #c8820c;
  background-color: #fff8ee;
}

.override-event-badge {
  background-color: #c8820c;
  color: white;
  padding: 2px 7px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.3px;
}

.timeline-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 4px;
}

.timeline-time {
  font-family: monospace;
  font-size: 12px;
  color: #8b4513;
  font-weight: 700;
  min-width: 140px;
}

.query-number {
  font-size: 13px;
  font-weight: 600;
  color: #555;
}

.state-badge {
  background-color: #d2b48c;
  color: #5d3a1a;
  padding: 2px 7px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 700;
  font-family: monospace;
  letter-spacing: 0.3px;
}

/* Confidence badges (AI queries) */
.confidence-badge {
  padding: 2px 7px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 700;
}

.conf-high         { background-color: #4caf50; color: white; }
.conf-medium       { background-color: #ffc107; color: #333; }
.conf-low          { background-color: #ff9800; color: white; }
.conf-insufficient { background-color: #9e9e9e; color: white; }

.digit-chip {
  background-color: #4a90d9;
  color: white;
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 12px;
  font-weight: 600;
}

/* Transcript fragment inside AI query */
.transcript-fragment {
  font-size: 13px;
  color: #555;
  background-color: #f0ede0;
  border-left: 2px solid #c8a870;
  padding: 4px 8px;
  margin: 6px 0;
  font-style: italic;
  line-height: 1.5;
}

.reasoning-text {
  font-size: 13px;
  color: #444;
  line-height: 1.5;
}

/* State machine event note */
.event-note {
  font-size: 13px;
  color: #444;
  line-height: 1.5;
}

/* ── Section E: Diagnostics ─────────────────────────────────────── */
.diagnostics-grid {
  display: grid;
  grid-template-columns: 130px 1fr;
  gap: 6px 16px;
  align-items: baseline;
}

.diag-key {
  color: #8b4513;
  font-weight: 600;
  font-size: 13px;
}

.diag-value {
  font-size: 13px;
  color: #333;
}

.diag-value.mono {
  font-family: monospace;
  font-size: 12px;
}

.diag-override {
  color: #c8820c;
  font-weight: 700;
}

/* ── Responsive ─────────────────────────────────────────────────── */
@media (max-width: 600px) {
  .record-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
  }

  .record-info {
    flex-direction: column;
    gap: 5px;
  }

  .channel-controls {
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }

  .channel-label {
    justify-content: center;
    min-width: 140px;
  }
}

</style>
